import React, { useEffect, useMemo, useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { formatDateOnlyLong, getLocalDateKey, getVisibleWeekDays } from '../utils/agendaDates';

export const OFFICE_CATEGORY_OPTIONS = [
    { label: 'Llamadas a clientes', value: 'calls' },
    { label: 'Cotizaciones y propuestas', value: 'quotes' },
    { label: 'Cobros y cartera', value: 'collections' },
    { label: 'Seguimiento comercial', value: 'followUp' },
    { label: 'Administración', value: 'administration' },
    { label: 'Planificación', value: 'planning' },
    { label: 'Otro trabajo de oficina', value: 'other' }
];

const emptyForm = {
    plannedDate: '',
    plannedStartTime: '09:00',
    plannedEndTime: '10:00',
    officeCategory: 'followUp',
    officeNotes: ''
};

const OfficeActivityDialog = ({
    visible,
    mode = 'planned',
    selectedDate,
    weekDays,
    activity,
    loading = false,
    onHide,
    onSubmit
}) => {
    const [form, setForm] = useState(emptyForm);
    const [errors, setErrors] = useState({});
    const isStartNow = mode === 'startNow';
    const dayOptions = useMemo(
        () => (weekDays?.length ? weekDays : getVisibleWeekDays(selectedDate)).map((day) => ({
            label: `${day.label}, ${formatDateOnlyLong(getLocalDateKey(day.date))}`,
            value: getLocalDateKey(day.date)
        })),
        [selectedDate, weekDays]
    );

    useEffect(() => {
        if (!visible) return;
        const selectedDateKey = getLocalDateKey(selectedDate);
        const defaultDate = dayOptions.some((option) => option.value === selectedDateKey)
            ? selectedDateKey
            : dayOptions[0]?.value || selectedDateKey;
        setForm({
            ...emptyForm,
            plannedDate: activity?.plannedDate || defaultDate,
            plannedStartTime: activity?.plannedStartTime || '09:00',
            plannedEndTime: activity?.plannedEndTime || '10:00',
            officeCategory: activity?.officeCategory || 'followUp',
            officeNotes: activity?.officeNotes || ''
        });
        setErrors({});
    }, [activity, dayOptions, selectedDate, visible]);

    const updateField = (field, value) => {
        setForm((current) => ({ ...current, [field]: value }));
        setErrors((current) => ({ ...current, [field]: null }));
    };

    const handleSubmit = () => {
        const nextErrors = {};
        if (!form.officeCategory) nextErrors.officeCategory = 'Selecciona el tipo de trabajo.';
        if (!isStartNow && !form.plannedDate) nextErrors.plannedDate = 'Selecciona el día.';
        if (!isStartNow && !form.plannedStartTime) nextErrors.plannedStartTime = 'Indica la hora de inicio.';
        if (!isStartNow && !form.plannedEndTime) nextErrors.plannedEndTime = 'Indica la hora final.';
        if (
            !isStartNow &&
            form.plannedStartTime &&
            form.plannedEndTime &&
            form.plannedEndTime <= form.plannedStartTime
        ) {
            nextErrors.plannedEndTime = 'La hora final debe ser posterior a la inicial.';
        }
        if (form.officeNotes.trim().length > 2000) nextErrors.officeNotes = 'Máximo 2000 caracteres.';
        setErrors(nextErrors);
        if (Object.keys(nextErrors).length) return;

        const payload = {
            officeCategory: form.officeCategory,
            officeNotes: form.officeNotes.trim() || undefined,
            ...(!isStartNow ? {
                plannedDate: form.plannedDate,
                plannedStartTime: form.plannedStartTime,
                plannedEndTime: form.plannedEndTime
            } : {})
        };
        onSubmit(payload);
    };

    const footer = (
        <div className="flex flex-column sm:flex-row justify-content-end gap-2">
            <Button type="button" label="Cancelar" className="p-button-text" onClick={onHide} disabled={loading} />
            <Button
                type="button"
                label={isStartNow ? 'Iniciar tiempo de oficina' : activity ? 'Guardar cambios' : 'Agregar a la agenda'}
                icon={isStartNow ? 'pi pi-play' : 'pi pi-check'}
                onClick={handleSubmit}
                loading={loading}
                disabled={loading}
            />
        </div>
    );

    return (
        <Dialog
            className="ventas-dialog ventas-office-dialog"
            header={isStartNow ? 'Iniciar tiempo de oficina' : activity ? 'Editar tiempo de oficina' : 'Planificar tiempo de oficina'}
            visible={visible}
            onHide={onHide}
            modal
            draggable={false}
            footer={footer}
            style={{ width: '36rem' }}
            breakpoints={{ '640px': '96vw' }}
        >
            <div className="grid p-fluid">
                {!isStartNow ? (
                    <>
                        <div className="col-12">
                            <label className="block font-medium mb-2">Día</label>
                            <Dropdown value={form.plannedDate} options={dayOptions} onChange={(event) => updateField('plannedDate', event.value)} />
                            {errors.plannedDate ? <small className="p-error">{errors.plannedDate}</small> : null}
                        </div>
                        <div className="col-12 md:col-6">
                            <label className="block font-medium mb-2">Hora de inicio</label>
                            <InputText type="time" value={form.plannedStartTime} onChange={(event) => updateField('plannedStartTime', event.target.value)} />
                            {errors.plannedStartTime ? <small className="p-error">{errors.plannedStartTime}</small> : null}
                        </div>
                        <div className="col-12 md:col-6">
                            <label className="block font-medium mb-2">Hora de finalización</label>
                            <InputText type="time" value={form.plannedEndTime} onChange={(event) => updateField('plannedEndTime', event.target.value)} />
                            {errors.plannedEndTime ? <small className="p-error">{errors.plannedEndTime}</small> : null}
                        </div>
                    </>
                ) : (
                    <div className="col-12">
                        <div className="surface-100 border-round p-3 text-color-secondary">
                            La hora de inicio se tomará del servidor. No se solicitará GPS ni reporte comercial.
                        </div>
                    </div>
                )}
                <div className="col-12">
                    <label className="block font-medium mb-2">Tipo de trabajo</label>
                    <Dropdown value={form.officeCategory} options={OFFICE_CATEGORY_OPTIONS} onChange={(event) => updateField('officeCategory', event.value)} />
                    {errors.officeCategory ? <small className="p-error">{errors.officeCategory}</small> : null}
                </div>
                <div className="col-12">
                    <label className="block font-medium mb-2">Nota opcional</label>
                    <InputTextarea
                        value={form.officeNotes}
                        onChange={(event) => updateField('officeNotes', event.target.value)}
                        rows={4}
                        autoResize
                        maxLength={2000}
                        placeholder="Ej.: preparar cotizaciones y dar seguimiento a clientes pendientes."
                    />
                    {errors.officeNotes ? <small className="p-error">{errors.officeNotes}</small> : null}
                </div>
            </div>
        </Dialog>
    );
};

export default OfficeActivityDialog;
